/**
 * /admin/personas — persona distribution + stuck-state operations
 * triage page. Powered by `GET /api/admin/personas/overview` for the
 * top tile + `GET /api/admin/personas?persona=<x>` for the deep-dive
 * table.
 *
 * Three reasons admins need this page:
 *   1. **Data**     — "what does our user base actually look like?"
 *                     Answer the question without spreadsheet exports.
 *   2. **Operations** — surface stuck-state callouts (transporters
 *                       who never finished Stripe; buyers with a Pass
 *                       but no watchlist; breeders never listing).
 *                       Each is a one-click message-the-user CTA.
 *   3. **Moderation** — filter to one persona to spot anomalies
 *                       (e.g., 50 fresh accounts with admin role —
 *                       you want to see THAT).
 */
import React, { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import AdminUserDetailsDialog from "@/components/AdminUserDetailsDialog";
import { Button } from "@/components/ui/button";
import {
  Tabs, TabsList, TabsTrigger,
} from "@/components/ui/tabs";
import { TruckIcon as Truck, HouseIcon as House, HeartStraightIcon as HeartStraight, StorefrontIcon as Storefront, ShieldStarIcon as ShieldStar, ChartBarIcon as ChartBar, CaretRightIcon as CaretRight, AirplaneTiltIcon as AirplaneTilt, ScissorsIcon as Scissors, MagnifyingGlassIcon as MagnifyingGlass } from "@phosphor-icons/react";
import { formatApiError } from "@/lib/utils-app";
import { toast } from "sonner";

const PERSONA_META = {
  transporter: { label: "Transporters", icon: Truck,         tone: "#C1272D" },
  international_transporter: { label: "Intl Couriers", icon: AirplaneTilt, tone: "#C9A227" },
  service_provider: { label: "Service Pros", icon: Scissors, tone: "#0D9488" },
  storefront_seller: { label: "Storefronts", icon: Storefront, tone: "#7C3AED" },
  breeder:     { label: "Breeders",     icon: House,         tone: "#8A6A00" },
  dog_owner:   { label: "Dog Owners",   icon: HeartStraight, tone: "#0EA5A0" },
  buyer:       { label: "Buyers",       icon: MagnifyingGlass, tone: "#3B82F6" },
  admin:       { label: "Admins",       icon: ShieldStar,    tone: "#0E0E0E" },
};

export default function AdminPersonas() {
  const [overview, setOverview] = useState(null);
  const [err, setErr] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  // Deep-dive state lifted to the page so the persona-count cards
  // and the URL `?stuck=` deep-link (from /admin/ops) can both drive
  // it. `stuck` overrides `persona` when present — the backend mirrors
  // this precedence.
  const [persona, setPersona] = useState("transporter");
  const [stuck, setStuckState] = useState(() => searchParams.get("stuck"));

  const setStuck = useCallback((key) => {
    setStuckState(key);
    // Sync to the URL so refreshes / back-buttons keep state.
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (key) next.set("stuck", key);
      else next.delete("stuck");
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  // When the URL `?stuck=` param changes externally (back/forward),
  // mirror it into local state.
  useEffect(() => {
    const fromUrl = searchParams.get("stuck");
    if (fromUrl !== stuck) setStuckState(fromUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const [detailUserId, setDetailUserId] = useState(null);
  const deepDiveRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    api.get("/admin/personas/overview")
      .then((r) => { if (!cancelled) setOverview(r.data); })
      .catch((e) => { if (!cancelled) setErr(formatApiError(e)); });
    return () => { cancelled = true; };
  }, []);

  // Scroll the deep-dive into view smoothly whenever the persona or
  // stuck filter changes via a click on the cards/triage rows. We
  // don't scroll on initial mount because the page should land at the
  // top — UNLESS we arrived via a deep-link (?stuck=...) in which
  // case scrolling straight to the deep dive is the right behavior.
  const didMountScroll = useRef(false);
  useEffect(() => {
    if (!didMountScroll.current) {
      didMountScroll.current = true;
      // On initial mount, if we have a `stuck` param from the URL,
      // scroll into the deep dive after the data loads.
      if (stuck) {
        setTimeout(() => {
          deepDiveRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 200);
      }
      return;
    }
    deepDiveRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [persona, stuck]);

  const driveToPersona = useCallback((p) => {
    setStuck(null);
    setPersona(p);
  }, [setStuck]);

  return (
    <AppShell>
      <div className="mb-8">
        <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold mb-3 flex items-center gap-2">
          <ChartBar size={14} weight="fill" className="text-gold" />
          Admin · User intelligence
        </p>
        <h1 className="font-spectral font-light text-4xl sm:text-5xl tracking-tight leading-[1.05]">
          Who's using the registry?
        </h1>
        <p className="text-sm text-[#6B6B6B] mt-3 max-w-2xl leading-relaxed">
          Persona distribution + drill-in. The same resolver drives the mobile
          bottom nav, so when a user transitions from buyer → breeder in real
          life, they move here automatically. Operational triage (stuck users,
          stuck payments, automation health) lives on the{" "}
          <a href="/admin/operations" className="text-ink underline decoration-gold/50 hover:decoration-gold">
            Operations Hub
          </a>.
        </p>
      </div>

      {err && (
        <div className="bg-[#FEE2E2] border border-[#C1272D]/30 text-[#C1272D] rounded-xl p-4 mb-6">
          {err}
        </div>
      )}

      {overview && (
        <PersonaCountsGrid
          counts={overview.counts}
          total={overview.total_active_users}
          multi={overview.multi_role_users}
          rosters={overview.rosters}
          onPick={driveToPersona}
        />
      )}

      <PersonaDeepDive
        ref={deepDiveRef}
        persona={persona}
        setPersona={driveToPersona}
        stuck={stuck}
        setStuck={setStuck}
        onPickUser={(id) => setDetailUserId(id)}
      />

      <AdminUserDetailsDialog
        userId={detailUserId}
        open={!!detailUserId}
        onClose={() => setDetailUserId(null)}
      />
    </AppShell>
  );
}

function PersonaCountsGrid({ counts, total, multi, rosters, onPick }) {
  const keys = ["transporter", "international_transporter", "service_provider", "storefront_seller", "breeder", "dog_owner", "buyer", "admin"];
  const r = rosters || {};
  // Ground-truth roster sub-lines: pros can exist without a user account
  // (public apply forms), so the resolver count alone under-represents them.
  const rosterSub = {
    transporter: r.transporters_roster != null
      ? `${r.transporters_roster} on roster${r.transporters_no_account ? ` · ${r.transporters_no_account} no account` : ""}`
      : null,
    international_transporter: r.intl_couriers_roster != null
      ? `${r.intl_couriers_roster} in directory${r.intl_couriers_no_account ? ` · ${r.intl_couriers_no_account} no account` : ""}`
      : null,
    service_provider: r.service_providers_approved != null
      ? [
          `${r.service_providers_approved} approved`,
          r.service_providers_pending ? `${r.service_providers_pending} pending` : null,
          r.service_providers_never_applied ? `${r.service_providers_never_applied} never applied` : null,
        ].filter(Boolean).join(" · ")
      : null,
    storefront_seller: r.storefronts_active != null
      ? `${r.storefronts_active} active stores`
      : null,
  };
  return (
    <div className="bg-white border border-[#E5E5E5] rounded-2xl p-5 sm:p-6 mb-6 shadow-[0_8px_30px_rgba(0,0,0,0.04)]">
      <div className="flex items-baseline justify-between mb-4">
        <h2 className="font-spectral text-2xl tracking-tight">Distribution</h2>
        <div className="text-[12px] text-[#6B6B6B]">
          <span className="font-bold text-ink tabular-nums">{total}</span> active users
          <span className="mx-2 opacity-40">·</span>
          <span className="font-bold text-ink tabular-nums">{multi}</span> wear ≥2 hats
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {keys.map((k) => {
          const m = PERSONA_META[k];
          const n = counts?.[k] || 0;
          const pct = total > 0 ? Math.round((n / total) * 100) : 0;
          const Icon = m.icon;
          return (
            <button
              key={k}
              type="button"
              onClick={() => onPick?.(k)}
              className="text-left rounded-xl border border-[#E5E5E5] hover:border-gold/55 hover:shadow-[0_6px_20px_rgba(193,162,39,0.10)] active:scale-[0.98] p-4 flex flex-col items-start gap-1.5 transition-all bg-white group"
              data-testid={`persona-count-${k}`}
              aria-label={`Show ${m.label} in deep dive`}
            >
              <span
                className="inline-flex items-center justify-center h-8 w-8 rounded-full flex-shrink-0"
                style={{ backgroundColor: `${m.tone}15`, color: m.tone }}
              >
                <Icon size={16} weight="duotone" />
              </span>
              <span className="font-spectral text-3xl tabular-nums leading-none text-ink">{n}</span>
              <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#6B6B6B]">
                {m.label}
              </span>
              <span className="text-[10px] text-[#9CA3AF]">
                {pct}% of base
                <span className="ml-1 opacity-0 group-hover:opacity-100 transition-opacity text-gold-dark">
                  · view →
                </span>
              </span>
              {rosterSub[k] && (
                <span className="text-[10px] font-semibold" style={{ color: m.tone }} data-testid={`persona-roster-${k}`}>
                  {rosterSub[k]}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// `StuckStatesCard` was relocated to /admin/ops on 2026-02-26.
// The triage buckets now live alongside stuck-payments / fulfillment-blocked
// / kennel-verifications under one operator-action hub.



const STUCK_LABELS = {
  transporters_pending_stripe_48h: "Transporters pending Stripe Connect (48h+)",
  service_providers_pending_stripe_48h: "Service providers pending Stripe Connect (48h+)",
  storefronts_pending_stripe_48h: "Storefronts pending Stripe Connect (48h+)",
  buyer_pass_no_watchlist_7d: "Buyer-Pass subscribers without watchlist (7d+)",
  breeders_no_listings_30d: "Breeders without marketplace listings (30d+)",
};

const PersonaDeepDive = React.forwardRef(function PersonaDeepDive(
  { persona, setPersona, stuck, setStuck, onPickUser },
  ref,
) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [quiet, setQuiet] = useState(false);

  const load = useCallback((p, s, q) => {
    setLoading(true);
    const quietParam = q ? "&quiet=14" : "";
    const url = s
      ? `/admin/personas?stuck=${encodeURIComponent(s)}&limit=200${quietParam}`
      : `/admin/personas?persona=${p}&limit=50${quietParam}`;
    api.get(url)
      .then((r) => setData(r.data))
      .catch(() => setData({ users: [], total: 0 }))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(persona, stuck, quiet); }, [persona, stuck, quiet, load]);

  return (
    <div
      ref={ref}
      className="bg-white border border-[#E5E5E5] rounded-2xl p-5 sm:p-6 shadow-[0_8px_30px_rgba(0,0,0,0.04)] scroll-mt-20"
      data-testid="persona-deep-dive"
    >
      <div className="flex items-baseline justify-between mb-4 gap-3 flex-wrap">
        <h2 className="font-spectral text-2xl tracking-tight">Deep dive</h2>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => setQuiet((v) => !v)}
            className={`text-[11px] font-bold uppercase tracking-[0.1em] rounded-full px-3 py-1.5 border transition-colors ${
              quiet
                ? "bg-[#C1272D] text-white border-[#C1272D]"
                : "bg-white text-[#6B6B6B] border-[#DDD] hover:border-ink"
            }`}
            data-testid="deep-dive-quiet-toggle"
            title="Only show members not seen in 14+ days"
          >
            {quiet ? "✓ " : ""}Gone quiet 14+ days
          </button>
          {data && (
            <span className="text-[11px] text-[#6B6B6B]">
              <span className="font-bold text-ink tabular-nums">{data.total ?? 0}</span> users
            </span>
          )}
        </div>
      </div>

      {/* Stuck-mode banner — only when user drilled in from triage. */}
      {stuck ? (
        <div
          className="flex items-center justify-between gap-3 mb-4 rounded-xl border border-[#C1272D]/30 bg-[#FEE2E2]/50 px-4 py-2.5"
          data-testid="deep-dive-stuck-banner"
        >
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#C1272D]">
              Operations triage
            </p>
            <p className="text-[13px] text-ink font-semibold truncate">
              {STUCK_LABELS[stuck] || stuck}
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setStuck?.(null)}
            className="rounded-full text-[11px] h-8 px-3 flex-shrink-0"
            data-testid="deep-dive-clear-stuck"
          >
            Clear filter
          </Button>
        </div>
      ) : (
        <Tabs value={persona} onValueChange={setPersona}>
          <TabsList
            className="bg-[#FAFAFA] border border-[#E5E5E5] rounded-2xl p-1.5 flex flex-wrap gap-1 mb-4 text-[12px] w-full h-auto justify-start"
            data-testid="deep-dive-tabs"
          >
            {Object.keys(PERSONA_META).map((p) => (
              <TabsTrigger
                key={p}
                value={p}
                className="rounded-full px-3 py-1.5 data-[state=active]:bg-white data-[state=active]:text-ink data-[state=active]:shadow-sm whitespace-nowrap"
                data-testid={`deep-dive-tab-${p}`}
              >
                {PERSONA_META[p].label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      )}

      {loading ? (
        <p className="text-sm text-[#6B6B6B] py-6 text-center">Loading…</p>
      ) : data?.users?.length ? (
        <div className="space-y-2">
          {data.users.map((u) => (
            <UserRow
              key={u.id}
              user={u}
              onPick={() => onPickUser?.(u.id)}
              showReclassify={stuck === "breeders_no_listings_30d"}
              onReclassified={() => {
                // Optimistic: drop the row from the visible list.
                setData?.((prev) => prev && ({
                  ...prev,
                  users: prev.users.filter((row) => row.id !== u.id),
                }));
              }}
            />
          ))}
        </div>
      ) : (
        <p className="text-sm text-[#6B6B6B] py-6 text-center">
          {stuck ? "No stuck users in this bucket — nice work." : "No users in this persona yet."}
        </p>
      )}
    </div>
  );
});

function daysAgo(iso) {
  if (!iso) return null;
  const d = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  return Number.isFinite(d) ? Math.max(0, d) : null;
}

function lastSeenLabel(user) {
  const seen = daysAgo(user.last_login_at);
  if (seen == null) {
    const age = daysAgo(user.created_at);
    return age == null ? "" : `joined ${age === 0 ? "today" : `${age}d ago`} · never logged back in`;
  }
  if (seen === 0) return "seen today";
  return `seen ${seen}d ago${seen >= 14 ? " ⚠" : ""}`;
}

function sourceLabel(user) {
  const s = user.signup_source || {};
  const utm = s.utm_source || s.utm_campaign;
  if (utm) return `via ${utm}${s.utm_campaign && s.utm_source ? ` (${s.utm_campaign})` : ""}`;
  if (s.referrer) {
    try { return `via ${new URL(s.referrer).hostname.replace("www.", "")}`; } catch { return "via link"; }
  }
  if (user.signup_platform && user.signup_platform !== "web") return `via ${user.signup_platform} app`;
  return "";
}

function UserRow({ user, onPick, showReclassify = false, onReclassified }) {
  const [busy, setBusy] = useState(false);
  const sig = user.signals || {};
  const tags = [];
  if (sig.is_transporter) tags.push("Transporter");
  if (sig.is_transporter_pending) tags.push("Stripe pending");
  if (sig.dogs_count) tags.push(`${sig.dogs_count} dogs`);
  if (sig.litters_count) tags.push(`${sig.litters_count} litters`);
  if (sig.has_buyer_pass) tags.push("Buyer Pass");
  if (sig.dog_log_active) tags.push("Dog Log");
  // Multi-hat visibility for admin: show each EXTRA persona this user
  // holds beyond their primary, so misclassifications and 2-hat members
  // are obvious at a glance.
  (user.available || [])
    .filter((p) => p !== user.persona && p !== "admin")
    .forEach((p) => tags.push(`+${p.replace("_", " ")}`));

  // The inverse-direction button: shown when this user is currently
  // classified as a dog owner BUT has real breeding signals (any
  // litter, or many dogs) — those are users who probably mis-picked
  // "dog owner" at signup. One click flips them back to breeder so
  // they re-enter the marketplace funnel + triage.
  const isDogOwnerWithBreedingSignals =
    user.persona === "dog_owner" &&
    ((sig.litters_count || 0) >= 1 || (sig.dogs_count || 0) >= 3);

  const reclassify = async (e, targetPersona, copy) => {
    e.stopPropagation();
    if (!window.confirm(copy)) return;
    setBusy(true);
    try {
      await api.post(`/admin/personas/users/${user.id}/reclassify`, { persona: targetPersona });
      const LBL = { breeder: "Breeder", owner: "Dog Owner", dog_owner: "Dog Owner", buyer: "Buyer" };
      toast.success(`${user.name || user.email} marked as ${LBL[targetPersona] || targetPersona}.`);
      onReclassified?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reclassify failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="w-full flex items-center gap-3 rounded-lg border border-[#EAEAEA] hover:border-ink/40 hover:bg-[#FAFAFA] px-3 py-2.5 transition-all"
      data-testid={`persona-user-${user.id}`}
    >
      <button
        type="button"
        onClick={onPick}
        className="flex-1 min-w-0 text-left flex items-center gap-3 active:scale-[0.99]"
        aria-label={`Open details for ${user.name || user.email}`}
      >
        <span className="flex-1 min-w-0">
          <span className="block text-[13px] font-semibold text-ink truncate">
            {user.name || user.email || "(unnamed)"}
          </span>
          <span className="block text-[11px] text-[#6B6B6B] truncate">{user.email}</span>
          <span className="block text-[10px] text-[#9CA3AF] truncate" data-testid={`user-pulse-${user.id}`}>
            {lastSeenLabel(user)}{sourceLabel(user) ? ` · ${sourceLabel(user)}` : ""}
          </span>
        </span>
        <div className="hidden sm:flex flex-wrap gap-1 max-w-[40%] justify-end">
          {tags.slice(0, 4).map((t) => (
            <span
              key={t}
              className="text-[10px] uppercase tracking-[0.1em] font-bold bg-[#FBF7EC] text-gold-dark border border-[#E8D9A6] rounded-full px-2 py-0.5"
            >
              {t}
            </span>
          ))}
        </div>
        {user.role === "admin" && (
          <span className="text-[10px] uppercase tracking-[0.1em] font-bold bg-ink text-gold rounded-full px-2 py-0.5 flex-shrink-0">
            Admin
          </span>
        )}
      </button>
      {showReclassify && user.role !== "admin" && (
        <button
          type="button"
          onClick={(e) => reclassify(
            e,
            "owner",
            `Mark ${user.name || user.email} as a Dog Owner (not a breeder)?\n\n` +
            `They'll be removed from the "Breeders without marketplace listings" triage immediately. ` +
            `If you're wrong, they can re-pick "Breeder" in Settings → "What brings you here?"`
          )}
          disabled={busy}
          title="Mark as Dog Owner — removes from breeder triage"
          data-testid={`reclassify-dog-owner-${user.id}`}
          className="text-[10px] uppercase tracking-[0.08em] font-bold text-crimson hover:text-white hover:bg-crimson border border-crimson/40 rounded-full px-2.5 py-1 transition-colors disabled:opacity-50 flex-shrink-0"
        >
          {busy ? "…" : "Not a breeder"}
        </button>
      )}
      {isDogOwnerWithBreedingSignals && user.role !== "admin" && (
        <button
          type="button"
          onClick={(e) => reclassify(
            e,
            "breeder",
            `Mark ${user.name || user.email} as a Breeder?\n\n` +
            `They're currently classified as Dog Owner but have ${sig.litters_count || 0} litter(s) and ${sig.dogs_count || 0} dog(s) on file. ` +
            `Flipping them re-enables breeder paywalls + brings them back into the marketplace funnel. ` +
            `If wrong, they (or you) can revert from Settings.`
          )}
          disabled={busy}
          title="They look like a real breeder — mark as Breeder"
          data-testid={`reclassify-breeder-${user.id}`}
          className="text-[10px] uppercase tracking-[0.08em] font-bold text-emerald-700 hover:text-white hover:bg-emerald-700 border border-emerald-300 rounded-full px-2.5 py-1 transition-colors disabled:opacity-50 flex-shrink-0"
        >
          {busy ? "…" : "Mark as breeder"}
        </button>
      )}
      {user.role !== "admin" && (
        <select
          value=""
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => {
            const v = e.target.value;
            if (!v) return;
            const label = { owner: "Dog Owner", breeder: "Breeder", buyer: "Buyer" }[v];
            reclassify(
              e,
              v,
              `Set ${user.name || user.email}'s persona to ${label}?\n\n` +
              `Their dashboard, nav, and these analytics switch immediately. ` +
              `They can change it themselves anytime in Settings → "What brings you here?"`
            );
            e.target.value = "";
          }}
          disabled={busy}
          title="Set this member's persona"
          data-testid={`set-persona-${user.id}`}
          className="text-[10px] uppercase tracking-[0.08em] font-bold text-[#6B6B6B] border border-[#DDD] rounded-full px-2 py-1 bg-white hover:border-ink cursor-pointer disabled:opacity-50 flex-shrink-0"
        >
          <option value="">Set persona…</option>
          <option value="owner">Dog Owner</option>
          <option value="breeder">Breeder</option>
          <option value="buyer">Buyer</option>
        </select>
      )}
      <CaretRight size={14} weight="bold" className="text-[#9CA3AF] flex-shrink-0" />
    </div>
  );
}
